import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StandardTable } from '../../../../shared/components/standard-table/standard-table/standard-table';
import { OrderService } from '../../../../core/services/order.service';
import { AlertService } from '../../../../shared/components/alert/alert';
import { TranslatePipe } from '../../../../shared/pipes/translate.pipe';
import { TranslationService } from '../../../../core/services/translation.service';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-confirmed-order',
  standalone: true,
  imports: [CommonModule, StandardTable, FormsModule],
  templateUrl: './confirmed-order.html',
  styleUrls: ['./confirmed-order.css'],
})
export class ConfirmedOrder implements OnInit {
  columnList = [
    { name: 'column.no', key: 'no', sortable: false },
    { name: 'column.alphabet', key: 'alphabet', sortable: false },
    { name: 'column.number', key: 'number', sortable: false },
    { name: 'column.name', key: 'name', sortable: false },
    { name: 'column.address', key: 'address', sortable: false },
    { name: 'column.phone', key: 'phone', sortable: false },
    { name: 'column.remark', key: 'remark', sortable: false },
  ];

  tableDataFilters: any[] = [];
  totalItems  = 0;
  isLoading   = false;
  currentPage = 1;
  pageSize    = 10;
  searchQuery = '';
  selectedYear  = '';
  selectedMonth = '';
  selectedAlphabet = '';

  constructor(
    private orderService: OrderService,
    private cdr: ChangeDetectorRef,
    private alertService: AlertService,
    private translationService: TranslationService,
  ) {}

  ngOnInit(): void {
    const now = new Date();
    this.selectedYear  = now.getFullYear().toString();
    this.selectedMonth = (now.getMonth() + 1).toString().padStart(2, '0');
    this.loadOrders();
  }

  loadOrders(): void {
    this.isLoading = true;

    const dateParam =
      this.selectedYear && this.selectedMonth
        ? `${this.selectedYear}-${this.selectedMonth}`
        : '';

    const payload: any = {
      page:   this.currentPage.toString(),
      limit:  this.pageSize.toString(),
      search: this.searchQuery,
      alphabet: this.selectedAlphabet,
    };

    if (dateParam) {
     
      const start = new Date(`${dateParam}-01`);
      const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
      payload.startdate = start.toISOString().split('T')[0];
      payload.enddate = end.toISOString().split('T')[0];
    }

    this.orderService.getConfirmedOrders(payload).subscribe({
      next: (res: any) => {
        if (res.returncode === '300') {
          this.tableDataFilters = res.data.data.map((t: any, index: number) => this.transformOrder(t, index));
          this.totalItems = res.data.meta.totalItems ?? res.data.meta.total ?? 0;
        } else {
          this.tableDataFilters = [];
          this.totalItems = 0;
        }
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.tableDataFilters = [];
        this.totalItems = 0;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  private transformOrder(t: any, index: number) {
    return {
      ...t,
      no: (this.currentPage - 1) * this.pageSize + index + 1,
      remark: t.remark || '', 
    };
  }

  search(value: string): void {
    this.searchQuery = value;
    this.currentPage = 1;
    this.loadOrders();
  }

  onDateFilterChange(filter: { year: string; month: string }): void {
    this.selectedYear  = filter.year;
    this.selectedMonth = filter.month;
    this.currentPage   = 1;
    this.loadOrders();
  }

  onAlphabetFilterChange(alphabet: string): void {
    this.selectedAlphabet = alphabet;
    this.currentPage      = 1;
    this.loadOrders();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadOrders();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.loadOrders();
  }

  exportExcel(): void {
    const dateParam =
      this.selectedYear && this.selectedMonth
        ? `${this.selectedYear}-${this.selectedMonth}`
        : '';

    const payload: any = {};
    if (this.searchQuery) {
      payload.search = this.searchQuery;
    }

    if (this.selectedAlphabet) {
      payload.alphabet = this.selectedAlphabet;
    }

    if (dateParam) {
      const start = new Date(`${dateParam}-01`);
      const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
      payload.startdate = start.toISOString().split('T')[0];
      payload.enddate = end.toISOString().split('T')[0];
    }

    this.isLoading = true;
    this.cdr.detectChanges();

    this.orderService.getConfirmedOrdersExport(payload).subscribe({
      next: (res: any) => {
        if (res.returncode === '300') {
          const items = Array.isArray(res.data) ? res.data : (res.data?.data || []);
          
          const exportData = items.map((t: any, index: number) => ({
            'No.': index + 1,
            Alphabet: t.alphabet || '',
            Number: t.number || '',
            Name: t.name || '',
            Address: t.address || '',
            Phone: t.phone || '',
            Remark: t.remark || '',
          }));

          const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
          const workbook: XLSX.WorkBook = {
            Sheets: { 'Confirmed Orders': worksheet },
            SheetNames: ['Confirmed Orders'],
          };

          XLSX.writeFile(workbook, `Confirmed_Orders_${dateParam || 'All'}.xlsx`);
        } else {
          this.alertService.error('No data found for export');
        }
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.alertService.error('Failed to export Excel');
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }
}
